/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useMemo, useEffect } from 'react';
import { useThemePreference } from '@jira/router-resources__theme-preference/src/useThemePreference.tsx';
import { generateTheme } from '@atlaskit/atlassian-navigation/theme-generator';
import type { NavigationTheme } from '@atlaskit/atlassian-navigation/theme/types';
import { useThemeObserver, getTokenValue } from '@atlaskit/tokens';
import { useTheme as useGlobalThemeData } from '@jira/platform__controllers-global-theme/src/controllers/index.tsx';
import { addThemeColor } from './theme-color-meta-tag/index.tsx';
import { capLightness } from './colors/index.tsx';
import type { UseThemeType } from './types.tsx';

const caseInsensitiveEquals = (one: string, two: string) => one.toLowerCase() === two.toLowerCase();

const calculateBackground = (backgroundColor: string, isDarkMode: boolean) => {
	// White is a proxy for 'default' when setting accent colours, we really want to default to 'elevation.surface'
	if (caseInsensitiveEquals(backgroundColor, '#ffffff')) {
		// getTokenValue doesn't work in SSR thus hardcode, fix in: https://product-fabric.atlassian.net/browse/DSP-9781
		const fallback = isDarkMode ? '#1d2125' : '#ffffff';

		// Can't be getToken() as AK Nav needs a hex string. It parses it to determine the correct text color
		return getTokenValue('elevation.surface', fallback);
	}
	return isDarkMode ? capLightness(backgroundColor, 35) : backgroundColor;
};

const useIsDarkThemeResolver = () => {
	// The best source of theme info. But doesn't work in SSR, fix in: https://product-fabric.atlassian.net/browse/DSP-9781
	const { colorMode } = useThemeObserver();

	// This works in SSR, but we need the browser to resolve 'auto', so we are wrong in SSR if 'auto' resolves to 'dark'
	const { theme: preference } = useThemePreference();

	return __SERVER__ ? preference === 'dark' : colorMode === 'dark';
};

/**
 * Provides the Nav3 theme object
 */
export const useTheme: UseThemeType = (): NavigationTheme | undefined => {
	const [
		{
			navigationTheme: { usesCustomColours, backgroundColor, highlightColor },
		},
	] = useGlobalThemeData();

	const isDarkMode = useIsDarkThemeResolver();

	const bg = useMemo(
		() => calculateBackground(backgroundColor, isDarkMode),
		[backgroundColor, isDarkMode],
	);

	useEffect(() => {
		addThemeColor(bg);
	}, [bg]);

	if (!usesCustomColours) {
		return undefined;
	}

	return generateTheme({
		name: 'jira',
		backgroundColor: bg,
		highlightColor,
	});
};

/**
 * Provides the Nav4 theme object.
 *
 * This is exactly copied from the existing `useTheme` hook for Nav3.
 *
 * The only difference is that this hook returns `backgroundColor` and `highlightColor` directly
 * instead of returning the result of `generateTheme()`.
 */
export const useNav4Theme = (): { backgroundColor: string; highlightColor: string } | undefined => {
	/**
	 * The colors chosen by the site admin in the site settings
	 */
	const [
		{
			navigationTheme: { usesCustomColours, backgroundColor, highlightColor },
		},
	] = useGlobalThemeData();

	/**
	 * Whether the user is currently using dark mode,
	 * or their configured preference if this is SSR.
	 */
	const isDarkMode = useIsDarkThemeResolver();

	/**
	 * The adjusted background color.
	 *
	 * When the user is in dark mode, the lightness of the background color is capped.
	 */
	const bg = useMemo(
		() => calculateBackground(backgroundColor, isDarkMode),
		[backgroundColor, isDarkMode],
	);

	/**
	 * Creates / updates a theme-color meta tag:
	 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/meta/name/theme-color
	 *
	 * Some browsers will use this to change the color of their UI.
	 */
	useEffect(() => {
		addThemeColor(bg);
	}, [bg]);

	if (!usesCustomColours) {
		return undefined;
	}

	return {
		backgroundColor: bg,
		highlightColor,
	};
};
